import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { Article } from 'shared/models/legacy';
import { ArticleCard } from '~/components/article-card';
import { SeeMore } from '~/components/see-more';
import { DivisibleStack } from '~/components/divisible-stack';
import { ModuleType } from '~/utils/module-type';
import { ScopedProvider } from '@lemon8/web-app-shared/ssr';
import { $CurrentPageModule } from '~/models/current-page-module';
import { $CurrentUserPosts } from '@user/shared/atoms/current-user-posts';
import { useViewTracking } from '~/utils/use-view-tracking';
import { $CurrentAvailableUserOrNull, CurrentUserId } from '@user/shared/atoms/current-user';
import { EPrivacyStatus } from 'shared/models';
import UserPrivacyContent from '@user/body/post/privacy';
import { useRouteLoader } from '~/utils/use-route-loader';
import { $UserPostLoadmore } from 'shared/atoms/user/user-posts-loadmore';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { ClickPositionPrefix } from '~/utils/click-position';

import './style.scss';

export default function UserPostSection(): JSX.Element | null {
  const userId = useAtomValue(CurrentUserId);
  const user = useAtomValue($CurrentAvailableUserOrNull);
  const { items, hasMore, maxBeHotTime } = useAtomValue($CurrentUserPosts);

  const loader = useRouteLoader(
    'user-post-loadmore',
    {
      user_id: userId || '',
      max_behot_time: `${maxBeHotTime}`,
      count: 20,
    },
    {
      atomFamilies: {
        $UserPostLoadmore,
      },
    },
  );

  const observedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.related_article,
      });
    }, []),
  });

  const handleSeeMore = () => loader.load();

  if (user && user.privacyStatus === EPrivacyStatus.PrivateUser) {
    return <UserPrivacyContent />;
  }

  if (!items.length) {
    return null;
  }

  return (
    <section id="user-post" ref={observedRef}>
      <ScopedProvider
        atomsWithHydration={[
          [$CurrentPageModule, ModuleType.related_article],
          [$CurrentPositionPrefix, ClickPositionPrefix.none],
        ]}
      >
        <>
          <DivisibleStack wrapper="article-list" divider="article-list-divider">
            {items.map((article: Article, rank: number) => (
              <ArticleCard article={article} key={article.groupId} rank={rank} />
            ))}
          </DivisibleStack>
          {hasMore && <SeeMore onClickSeeMore={handleSeeMore} loading={loader.state === 'loading'} />}
        </>
      </ScopedProvider>
    </section>
  );
}
